import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { useHandleShowDialogs } from 'forms/hooks';
import { all } from 'ramda';
import { isNilOrEmpty, isNilOrEmptyString } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import useValidateFieldsPromise from '@gef-ui/form-core/hooks/form/useValidateFieldsPromise';
import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import Checkbox from '@gef-ui/form-material/Checkbox';
import { validationDisplayTypes } from '@gef-ui/form-material/constants';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import TextFieldWithSearchIcon from 'components/atoms/GefMui/TextFieldWithSearchIcon';
import { MaxLengths } from 'constants/forms';
import MODALS from 'constants/modals';
import dialog from 'features/common/dialog';
import { addSingleErrorByPageAndId, removeErrorByPageAndId } from 'features/common/submitFormDialog/slice';
import { searchUser } from 'features/other/resolverSettings/actions';
import { searchUserTypes } from 'features/other/resolverSettings/constants';
import { getUserDataFromDBFlag } from 'features/other/resolverSettings/selectors';
import { setUserDataDBFlag } from 'features/other/resolverSettings/slice';
import { tryAddingManager } from 'features/views/settings/marketPlaceAgencies/editAgencyModal/actions';
import { EDIT_AGENCY_MODAL_FORM_NAME, FIELDS } from 'features/views/settings/marketPlaceAgencies/editAgencyModal/form';
import UserSearchModal from 'pages/UserSearchModal/UserSearchModal';

export const UserFilter = ({ agency }) => {
	const dispatch = useDispatch();
	const validateFields = useValidateFieldsPromise();
	const formData = useFormData();

	const isUserDataFromDb = useSelector(getUserDataFromDBFlag);

	const handleSearchUser = useCallback(async () => {
		if (all(isNilOrEmptyString, [formData?.login, formData?.sellerIn, formData?.userSurname])) {
			dispatch(
				addSingleErrorByPageAndId({
					page: EDIT_AGENCY_MODAL_FORM_NAME,
					errors: ['loginOrSellerInOrUserSurname'],
				}),
			);
			dispatch(dialog.actions.open(EDIT_AGENCY_MODAL_FORM_NAME));
		} else {
			dispatch(removeErrorByPageAndId({ page: EDIT_AGENCY_MODAL_FORM_NAME, id: 'loginOrSellerInOrUserSurname' }));

			try {
				await validateFields([[FIELDS.login], [FIELDS.sellerIn], [FIELDS.userSurname]]);
				dispatch(searchUser(EDIT_AGENCY_MODAL_FORM_NAME, searchUserTypes.MARKET_PLACE_AGENCIES_SEARCH));
			} catch (error) {
				return;
			}
		}
	}, [dispatch, validateFields, formData?.login, formData?.sellerIn, formData?.userSurname]);

	const handleAddManager = useCallback(() => {
		dispatch(tryAddingManager({ agency }));
	}, [dispatch, agency]);

	const handleShowDialogsForAdd = useHandleShowDialogs({
		page: EDIT_AGENCY_MODAL_FORM_NAME,
		mapper: {
			loginOrSellerInOrUserSurname: ['login', 'sellerIn', 'userSurname'],
		},
		continueFn: handleAddManager,
	});

	const handleUserFieldChange = useCallback(() => {
		if (isUserDataFromDb) {
			dispatch(setUserDataDBFlag(false));
		}
	}, [dispatch, isUserDataFromDb]);

	return (
		<>
			<UserSearchModal id={MODALS.user_search} />
			<Grid container spacing={2}>
				<Grid size={3}>
					<TextFieldWithSearchIcon
						fieldName={FIELDS.login}
						label={getLabelMessage('resolverSettings:login')}
						onSearch={handleSearchUser}
						onChange={handleUserFieldChange}
						maxLength={MaxLengths.LOGIN}
						required={all(isNilOrEmpty)([formData?.sellerIn, formData?.userSurname])}
						validationDisplay={!formData?.login ? validationDisplayTypes.HIDDEN : undefined}
					/>
				</Grid>
				<Tooltip title={getLabelMessage('tooltips:sellerInRange')}>
					<Grid size={3}>
						<TextFieldWithSearchIcon
							fieldName={FIELDS.sellerIn}
							label={getLabelMessage('resolverSettings:sellerIn')}
							onSearch={handleSearchUser}
							onChange={handleUserFieldChange}
							maxLength={MaxLengths.SELLER_IN}
							required={all(isNilOrEmpty)([formData?.userSurname, formData?.login])}
							validationDisplay={!formData?.sellerIn ? validationDisplayTypes.HIDDEN : undefined}
						/>
					</Grid>
				</Tooltip>
				<Grid size={3}>
					<TextField
						size="small"
						fieldName={FIELDS.userFirstName}
						label={getLabelMessage('resolverSettings:userFirstName')}
						onChange={handleUserFieldChange}
						slotProps={{
							htmlInput: {
								maxLength: MaxLengths.NAME,
							},
						}}
					/>
				</Grid>
				<Grid size={3}>
					<TextFieldWithSearchIcon
						fieldName={FIELDS.userSurname}
						label={getLabelMessage('resolverSettings:userSurname')}
						onSearch={handleSearchUser}
						onChange={handleUserFieldChange}
						maxLength={MaxLengths.SURNAME}
						required={all(isNilOrEmpty)([formData?.sellerIn, formData?.login])}
						validationDisplay={!formData?.userSurname ? validationDisplayTypes.HIDDEN : undefined}
					/>
				</Grid>
				<Grid size={12}>
					<Grid container justifyContent="space-between">
						<Grid size={4}>
							<Checkbox
								fieldName={FIELDS.withSubordinates}
								label={getLabelMessage('settings:editAgencyModal.withSubordinates')}
							/>
						</Grid>
						<Grid size={4}>
							<SubmitButton
								variant="contained"
								onClick={handleAddManager}
								onClickError={handleShowDialogsForAdd}
								fullWidth
								text={getLabelMessage('common:add')}
							/>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</>
	);
};

UserFilter.propTypes = {
	agency: PropTypes.object,
};

export default UserFilter;
